// apps/provider/src/components/ai/FloatingActionButton.jsx

import { useState, useEffect } from 'react';
import {
  Mic, MicOff, Pause, Play, X, Minimize2, Maximize2,
  MessageSquare, Settings, FileText, Loader2, ChevronUp, LifeBuoy
} from 'lucide-react';
import { useScribeStore } from '../../stores/scribeStore';
import { useCopilotStore } from '../../stores/copilotStore';
import { useSupport } from '@/contexts/SupportContext';
import { useFeatureFlags } from '@/contexts/FeatureContext';
import { ScribePanel } from './ScribePanel';
import { CopilotPanel } from './CopilotPanel';
import { cn } from '@/lib/utils';

export function FloatingActionButton() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'scribe' | 'copilot' | null
  const [bottomOffset, setBottomOffset] = useState(6);
  const shouldOffset = location.pathname.includes('/exercises');
  const [hasRoutineBuilder, setHasRoutineBuilder] = useState(false);
  const isOnExercisePage = location.pathname.includes('/exercises');

  const {
    isRecording,
    isPaused,
    recordingDuration,
    isTranscribing,
    isGeneratingNote,
    isProcessing,
    stopRecording,
    pauseRecording,
    resumeRecording
  } = useScribeStore();

  const { isOpen: isCopilotOpen, toggleCopilot } = useCopilotStore();
  const { openSupport } = useSupport();
  const { features } = useFeatureFlags();

  // Format duration for display
  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
    }
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  // Handle main button click
  const handleMainClick = (e) => {
    e.stopPropagation();
    if (isRecording) {
      stopRecording();
      setActivePanel('scribe');
    } else if (activePanel) {
      setActivePanel(null);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  // Handle scribe button click — open panel in pre-recording state (template selection first)
  const handleScribeClick = () => {
    setIsExpanded(false);
    setActivePanel('scribe');
  };

  // Handle copilot button click
  const handleCopilotClick = () => {
    setIsExpanded(false);
    setActivePanel('copilot');
    toggleCopilot();
  };

  // Handle support button click
  const handleSupportClick = () => {
    setIsExpanded(false);
    openSupport();
  };

  // Close expanded menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is on the FAB itself
      const fabContainer = document.querySelector('.fab-container');
      if (isExpanded && fabContainer && !fabContainer.contains(e.target)) {
        setIsExpanded(false);
      }
    };

    // Use mousedown instead of click for better responsiveness
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  // Adjust position if other FABs are present
  useEffect(() => {
    if (!isOnExercisePage) {
      setHasRoutineBuilder(false);
      return;
    }

    // On exercise page, check for the routine builder
    const checkForBuilder = () => {
      // Try different selectors
      const builder = document.querySelector('.routine-builder-fab') ||
        document.querySelector('[class*="Dumbbell"]')?.closest('div.fixed');
      setHasRoutineBuilder(!!builder);
    };

    // Check immediately and on interval
    checkForBuilder();
    const interval = setInterval(checkForBuilder, 500);

    return () => clearInterval(interval);
  }, [location.pathname]);

  return (
    <>
      {/* Main FAB Container */}
      <div className="fab-container fixed bottom-[calc(1.5rem_+_var(--safe-bottom))] right-6 z-50 flex flex-col items-end gap-3 print:hidden">
        {/* Secondary Actions (shown when expanded) */}
        {isExpanded && !isRecording && !activePanel && (
          <div className="flex flex-col gap-2 animate-fadeInUp">
            {/* Scribe Button */}
            {features?.prism_scribe && (
              <button
                onClick={handleScribeClick}
                className="flex items-center gap-3 bg-white rounded-full shadow-lg px-4 py-3 hover:shadow-xl transition-shadow"
              >
                <Mic className="h-5 w-5 text-gray-700" />
                <span className="text-sm font-medium text-gray-700">Start Scribe</span>
              </button>
            )}

            {/* Copilot Button */}
            <button
              onClick={handleCopilotClick}
              className="flex items-center gap-3 bg-white rounded-full shadow-lg px-4 py-3 hover:shadow-xl transition-shadow"
            >
              <MessageSquare className="h-5 w-5 text-gray-700" />
              <span className="text-sm font-medium text-gray-700">Chat</span>
            </button>

            {/* Support Button */}
            <button
              onClick={handleSupportClick}
              className="flex items-center gap-3 bg-white rounded-full shadow-lg px-4 py-3 hover:shadow-xl transition-shadow"
            >
              <LifeBuoy className="h-5 w-5 text-gray-700" />
              <span className="text-sm font-medium text-gray-700">Support</span>
            </button>
          </div>
        )}

        {/* Recording Status (shown when recording) */}
        {isRecording && (
          <div className="flex items-center gap-3 bg-white rounded-full shadow-lg px-4 py-2 mb-2">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-gray-700">
                Recording {formatDuration(recordingDuration)}
              </span>
            </div>

            <div className="flex items-center gap-1">
              {isPaused ? (
                <button
                  onClick={resumeRecording}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Play className="h-4 w-4 text-gray-600" />
                </button>
              ) : (
                <button
                  onClick={pauseRecording}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Pause className="h-4 w-4 text-gray-600" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Processing Status */}
        {(isTranscribing || isGeneratingNote || isProcessing) && (
          <div className="flex items-center gap-2 bg-white rounded-full shadow-lg px-4 py-2 mb-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm font-medium text-gray-700">
              {isTranscribing ? 'Transcribing...' : isProcessing ? 'Processing...' : 'Generating note...'}
            </span>
          </div>
        )}

        {/* Main FAB Button */}
        <button
          onClick={handleMainClick}
          className={cn(
            "rounded-full shadow-lg hover:shadow-xl transition-all duration-200 p-4",
            isRecording ? "bg-red-500 hover:bg-red-600" : "bg-primary hover:bg-primary/90",
            activePanel && "bg-gray-600"
          )}
        >
          {isRecording ? (
            <MicOff className="h-6 w-6 text-white" />
          ) : activePanel ? (
            <X className="h-6 w-6 text-white" />
          ) : isExpanded ? (
            <X className="h-6 w-6 text-white" />
          ) : (
            <ChevronUp className="h-6 w-6 text-white" />
          )}
        </button>
      </div>

      {/* Scribe Panel */}
      {activePanel === 'scribe' && (
        <ScribePanel onClose={() => setActivePanel(null)} />
      )}

      {/* Copilot Panel */}
      {activePanel === 'copilot' && (
        <CopilotPanel onClose={() => setActivePanel(null)} />
      )}
    </>
  );
}